import React, { useState } from 'react';
import { Box, Typography, Button, Grid, useTheme, useMediaQuery, Divider, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colors, fonts } from '../../ColorFont/color';
import { url } from '../../ConstUrl/Url';
import {
    IconCheck,
    IconInfoCircle,
    IconArrowLeft
} from '@tabler/icons-react';
import NewHeaderNavWithSidebar from '../../components/HeaderNav/NewHeaderNavWithSidebar';
import CarbonCreditsCongratulations from './CarbonCreditsCongratulations';

/* 
===== UNIVERSAL TOKENS =====
Imported seamlessly from current theme mappings
============================
*/

const translations = {
    en: {
        review: "Review",
        reviewSubmit: "Review & Submit",
        verifyDetails: "Verify your project details",
        location: "Location",
        landSize: "Land Size",
        ownership: "Ownership",
        duration: "Duration",
        selectedPractices: "Selected Practices",
        estimatedCarbonCredits: "Estimated Carbon Credits",
        subjectToVerification: "Subject to verification",
        whatHappensNext: "What happens next?",
        verifierText: "A verifier will review your project. You'll need to upload proof of your sustainable practices.",
        submitProject: "Submit Project",
        back: "Back",
        projectName: "Project Name"
    },
    mr: {
        review: "पुनरावलोकन",
        reviewSubmit: "पुनरावलोकन आणि सबमिट करा",
        verifyDetails: "आपल्या प्रकल्प तपशीलांची पडताळणी करा",
        location: "ठिकाण",
        landSize: "जमिनीचे क्षेत्रफळ",
        ownership: "मालकी",
        duration: "कालावधी",
        selectedPractices: "निवडलेल्या पद्धती",
        estimatedCarbonCredits: "अंदाजित कार्बन क्रेडिट्स",
        subjectToVerification: "पडताळणीच्या अधीन",
        whatHappensNext: "पुढे काय होईल?",
        verifierText: "एक पंचाळणीकर्ता तुमच्या प्रकल्पाचे पुनरावलोकन करेल. तुम्हाला तुमच्या शाश्वत पद्धतींचा पुरावा अपलोड करावा लागेल.",
        submitProject: "प्रकल्प सबमिट करा",
        back: "मागे",
        projectName: "प्रकल्पाचे नाव"
    },
    hi: {
        review: "समीक्षा",
        reviewSubmit: "समीक्षा करें और सबमिट करें",
        verifyDetails: "अपने परियोजना विवरणों को सत्यापित करें",
        location: "स्थान",
        landSize: "भूमि का आकार",
        ownership: "स्वामित्व",
        duration: "अवधि",
        selectedPractices: "चयनित अभ्यास",
        estimatedCarbonCredits: "अनुमानित कार्बन क्रेडिट",
        subjectToVerification: "सत्यापन के अधीन",
        whatHappensNext: "आगे क्या होगा?",
        verifierText: "एक सत्यापनकर्ता आपकी परियोजना की समीक्षा करेगा। आपको अपने सतत प्रथाओं का प्रमाण अपलोड करना होगा।",
        submitProject: "प्रोजेक्ट सबमिट करें",
        back: "वापस",
        projectName: "प्रोजेक्ट का नाम"
    }
};

const TagFill = ({ text }) => {
    return (
        <Box sx={{
            backgroundColor: colors.carbon_bg_pill || '#B2EBF2',
            padding: '6px 12px',
            borderRadius: '6px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            <Typography sx={{
                fontFamily: fonts.publicSans || fonts.poppins,
                fontWeight: 600,
                fontSize: '12px',
                lineHeight: '16px',
                color: colors.carbon_text_pill || '#0097A7',
            }}>
                {text}
            </Typography>
        </Box>
    );
};

const DataLabel = ({ label, value }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <Typography sx={{
            fontFamily: fonts.publicSans || fonts.poppins,
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            color: colors.carbon_text_secondary || '#65758B'
        }}>
            {label}
        </Typography>
        <Typography sx={{
            fontFamily: fonts.publicSans || fonts.poppins,
            fontWeight: 500,
            fontSize: '15px',
            lineHeight: '22px',
            color: colors.carbon_text_main || '#0F1729'
        }}>
            {value}
        </Typography>
    </Box>
);

const CarbonCreditsReview = ({ userData, onNext, onBack }) => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language || 'en';
    const tLocal = translations[currentLang] || translations['en'];
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showCongrats, setShowCongrats] = useState(false);

    const steps = [
        { id: 1, label: "", isActive: true, isCompleted: true },
        { id: 2, label: "", isActive: true, isCompleted: true },
        { id: 3, label: "", isActive: true, isCompleted: true },
        { id: 4, label: tLocal.review, isActive: true, isCompleted: false },
    ];

    const [draftData, setDraftData] = useState({});

    React.useEffect(() => {
        const draft = JSON.parse(localStorage.getItem('carbonCreditDraft') || '{}');
        setDraftData(draft);
    }, []);

    // Format display data safely based on our previous mocked Figma details
    const displayData = {
        location: draftData.district ? `${draftData.village ? draftData.village + ', ' : ''}${draftData.district}, ${draftData.state}` : "Location pending",
        landSize: draftData.landSize ? `${draftData.landSize} ${draftData.unit || 'acres'}` : "Size pending",
        ownership: draftData.ownershipType || "Pending",
        duration: draftData.projectDuration || "12 months",
        practices: draftData.practices || [],
        credits: draftData.estimatedCredits || "0.0",
        projectName: draftData.projectName || "Unnamed Project"
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            const rawDraft = JSON.parse(localStorage.getItem('carbonCreditDraft') || '{}');
            
            // Restructure the flat draft data into the nested structure expected by the backend
            const payload = {
                userId: userData?._id || rawDraft.userId,
                projectName: rawDraft.projectName,           // top-level per schema
                farmDetails: {
                    state: rawDraft.state,
                    district: rawDraft.district,
                    taluka: rawDraft.taluka || '',
                    city: rawDraft.village || rawDraft.city || '',  // schema uses 'city'
                    gpsLocation: rawDraft.gpsLocation,
                    landSize: rawDraft.landSize,
                    unit: rawDraft.unit,
                    ownershipType: rawDraft.ownershipType
                },
                sustainablePractices: rawDraft.practices || [],
                estimatedCredits: rawDraft.estimatedCredits,
                timeline: {
                    startDate: rawDraft.projectStartDate,     // schema field: startDate
                    duration: rawDraft.projectDuration,       // schema field: duration
                    previousSeasonCrop: rawDraft.previousCrop,
                    additionalNotes: rawDraft.additionalNotes
                }
            };

            const response = await fetch(`${url}/api/carbon-credits/projects`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (response.ok) {
                localStorage.removeItem('carbonCreditDraft');
                setShowCongrats(true);
            } else {
                alert(data.message || "Failed to submit project.");
            }
        } catch (error) {
            console.error("Error submitting final review:", error);
            alert("An error occurred during final submission.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box sx={{
            width: '100%',
            minHeight: '100vh',
            backgroundColor: '#FFFFFF',
            pb: '80px'
        }}>
            {/* Top Stepper Area */}
            <Box sx={{
                width: '100%',
                pt: isMobile ? '12px' : '16px',
                pb: isMobile ? '12px' : '14px',
                borderBottom: `1px solid ${colors.carbon_border_solid || '#E0E5EB'}`,
                backgroundColor: '#FFFFFF',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center', // Centered vertically
                    justifyContent: 'space-between',
                    width: '100%',
                    maxWidth: '680px',
                    px: isMobile ? '20px' : '24px'
                }}>
                    {steps.map((step, index) => (
                        <React.Fragment key={step.id}>
                            <Box sx={{
                                width: '32px',
                                height: '32px',
                                flexShrink: 0,
                                borderRadius: '50%',
                                backgroundColor: (step.isActive || step.isCompleted) ? (colors.carbon_bg_main || '#00B4D8') : (colors.carbon_step_bg_unselected || '#E2E8F0'),
                                color: (step.isActive || step.isCompleted) ? '#FFFFFF' : (colors.carbon_step_text_unselected || '#64748B'),
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontFamily: fonts.publicSans || fonts.poppins,
                                fontWeight: 600,
                                fontSize: '14px',
                                zIndex: 2
                            }}>
                                {step.isCompleted ? <IconCheck size={18} color="#FFFFFF" stroke={2.5} /> : step.id}
                            </Box>

                            {index < steps.length - 1 && (
                                <Box sx={{
                                    flex: 1, // Stretch precisely
                                    mx: isMobile ? '8px' : '16px',
                                    height: '2px', // Thinner cleaner line
                                    backgroundColor: (steps[index + 1].isActive || steps[index + 1].isCompleted) ? (colors.carbon_bg_main || '#00B4D8') : (colors.carbon_step_bg_unselected || '#E2E8F0'),
                                    borderRadius: '4px'
                                }} />
                            )}
                        </React.Fragment>
                    ))}
                </Box>
                <Typography sx={{
                    mt: '16px',
                    fontFamily: fonts.publicSans || fonts.poppins,
                    fontWeight: 500,
                    fontSize: '13px',
                    color: colors.carbon_text_secondary || '#65758B',
                }}>
                    {steps.find(s => s.label)?.label || ""}
                </Typography>
            </Box>

            {/* Main Content */}
            <Box sx={{
                maxWidth: '680px',
                margin: '0 auto',
                px: 0,
                pt: isMobile ? '16px' : '24px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
            }}>
                {/* Header Section */}
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    mb: '24px'
                }}>
                    <Box sx={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '50%',
                        border: `3px solid ${colors.carbon_bg_main || '#00B4D8'}`,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}>
                        <IconCheck size={32} color={colors.carbon_bg_main || '#00B4D8'} stroke={2.5} />
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography sx={{
                            fontFamily: fonts.manrope || fonts.poppins,
                            fontWeight: 700,
                            fontSize: isMobile ? '24px' : '28px',
                            lineHeight: '34px',
                            color: colors.carbon_text_main || '#0F1729',
                            mb: '8px'
                        }}>
                            {tLocal.reviewSubmit}
                        </Typography>
                        <Typography sx={{
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontWeight: 400,
                            fontSize: isMobile ? '14px' : '16px',
                            lineHeight: '22px',
                            color: colors.carbon_text_secondary || '#65758B',
                            maxWidth: '480px'
                        }}>
                            {tLocal.verifyDetails}
                        </Typography>
                    </Box>
                </Box>

                {/* Main Details Card */}
                <Box sx={{
                    width: '100%',
                    backgroundColor: '#FFFFFF',
                    border: `1px solid ${colors.carbon_border_solid || '#E0E5EB'}`,
                    borderRadius: '12px',
                    boxShadow: '0px 1px 2px 0px rgba(0,0,0,0.05)',
                    padding: '24px',
                    mb: '24px'
                }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <DataLabel label={tLocal.projectName} value={displayData.projectName} />
                            <Divider sx={{ mt: '16px', borderColor: colors.carbon_border_solid || '#E0E5EB', opacity: 0.6 }} />
                        </Grid>
                        <Grid item xs={6}>
                            <DataLabel label={tLocal.location} value={displayData.location} />
                        </Grid>
                        <Grid item xs={6}>
                            <DataLabel label={tLocal.landSize} value={displayData.landSize} />
                        </Grid>
                        <Grid item xs={6}>
                            <DataLabel label={tLocal.ownership} value={displayData.ownership} />
                        </Grid>
                        <Grid item xs={6}>
                            <DataLabel label={tLocal.duration} value={displayData.duration} />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: '24px', borderColor: colors.carbon_border_solid || '#E0E5EB' }} />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <Typography sx={{
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontWeight: 400,
                            fontSize: '14px',
                            lineHeight: '20px',
                            color: colors.carbon_text_secondary || '#65758B'
                        }}>
                            {tLocal.selectedPractices}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                            {displayData.practices.length > 0 ? displayData.practices.map((practice, index) => (
                                <TagFill key={index} text={practice} />
                            )) : <Typography sx={{ fontSize: '14px', color: '#94A3B8' }}>No practices selected</Typography>}
                        </Box>
                    </Box>
                </Box>

                {/* Estimate Credits Solid Box */}
                <Box sx={{
                    width: '100%',
                    backgroundColor: colors.carbon_bg_estimated_solid || '#00A9CC',
                    borderRadius: '12px',
                    padding: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    mb: '24px'
                }}>
                    <Typography sx={{
                        fontFamily: fonts.publicSans || fonts.poppins,
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        color: 'rgba(255, 255, 255, 0.9)'
                    }}>
                        {tLocal.estimatedCarbonCredits}
                    </Typography>

                    {/* Parsing robustly if subscript is needed, based on Figma image 7.8 tCO2 is fully bold, 2 is subscripted implicitly through sizing or raw text block */}
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                        <Typography sx={{
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontWeight: 700,
                            fontSize: '36px',
                            lineHeight: '40px',
                            color: '#FFFFFF'
                        }}>
                            {displayData.credits} <Typography component="span" sx={{ fontSize: '24px', fontWeight: 700 }}>tCO</Typography><Typography component="span" sx={{ fontSize: '18px', fontWeight: 700 }}>₂e</Typography>
                        </Typography>
                    </Box>

                    <Typography sx={{
                        fontFamily: fonts.publicSans || fonts.poppins,
                        fontWeight: 400,
                        fontSize: '12px',
                        lineHeight: '16px',
                        color: 'rgba(255, 255, 255, 0.7)'
                    }}>
                        {tLocal.subjectToVerification}
                    </Typography>
                </Box>

                {/* Warning Alert Box */}
                <Box sx={{
                    width: '100%',
                    backgroundColor: colors.carbon_warning_bg || '#FFFDE7',
                    border: `1px solid ${colors.carbon_warning_border || '#FFF59D'}`,
                    borderRadius: '12px',
                    padding: '20px',
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: '16px',
                    alignItems: 'flex-start'
                }}>
                    <IconInfoCircle
                        size={24}
                        color={colors.carbon_warning_text || '#D84315'}
                        stroke={2}
                        style={{ flexShrink: 0, marginTop: '2px' }}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <Typography sx={{
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontWeight: 600,
                            fontSize: '14px',
                            lineHeight: '20px',
                            color: colors.carbon_warning_text || '#D84315'
                        }}>
                            {tLocal.whatHappensNext}
                        </Typography>
                        <Typography sx={{
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontWeight: 400,
                            fontSize: '14px',
                            lineHeight: '20px',
                            color: colors.carbon_warning_text || '#D84315',
                            opacity: 0.9
                        }}>
                            {tLocal.verifierText}
                        </Typography>
                    </Box>
                </Box>

                {/* Final Submit Button */}
                <Box sx={{ mt: '48px', mb: '40px', width: '100%', display: 'flex', justifyContent: 'center', gap: '16px', flexDirection: isMobile ? 'column' : 'row' }}>
                    <Button
                        onClick={onBack}
                        variant="outlined"
                        startIcon={<IconArrowLeft size={18} stroke={2.5} />}
                        sx={{
                            borderRadius: '50px',
                            px: { xs: 3, md: 6 },
                            height: '52px',
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontWeight: 700,
                            fontSize: '15px',
                            textTransform: 'none',
                            color: colors.carbon_text_main || '#1C252E',
                            borderColor: colors.carbon_border_solid || '#E0E5EB',
                            minWidth: { sm: '180px' },
                            '&:hover': {
                                borderColor: colors.carbon_border_selected || '#919EAB',
                                backgroundColor: '#F4F6F8',
                            },
                        }}
                    >
                        {tLocal.back}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        sx={{
                            borderRadius: '50px',
                            px: { xs: 3, md: 6 },
                            height: '52px',
                            backgroundColor: colors.carbon_bg_main || '#00B4D8',
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontWeight: 700,
                            fontSize: '15px',
                            textTransform: 'none',
                            color: '#FFFFFF',
                            minWidth: { sm: '220px' },
                            boxShadow: '0px 4px 12px rgba(0, 180, 216, 0.2)',
                            '&:hover': {
                                backgroundColor: '#009DC0',
                                boxShadow: '0px 6px 16px rgba(0, 180, 216, 0.3)',
                                transform: 'translateY(-1px)'
                            },
                        }}
                    >
                        {isSubmitting ? <CircularProgress size={24} color="inherit" /> : tLocal.submitProject}
                    </Button>
                </Box>

                <CarbonCreditsCongratulations 
                    open={showCongrats} 
                    onClose={() => {
                        setShowCongrats(false);
                        if (onNext) onNext();
                    }} 
                />

            </Box>
        </Box>
    );
};

export default CarbonCreditsReview;
